/**
 * The group invite an invitee opened before they could accept it — signed out, or a brand-new user who first has
 * to get a Symphony account (T36). The invite page saves it; `usePendingGroupResume` brings the reader back once
 * the account exists (not after onboarding: 2026-11-02); reaching any final state on the invite page clears it.
 *
 * localStorage, because the trip runs through the Wix sign-in page and a full reload. A new key, so no `orion-web:`
 * prefix (orion-web CLAUDE.md). Expires after a day: an invite parked and never finished must not hijack a later
 * session.
 */
const KEY = 'symphony-pending-group-invite';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function savePendingGroupInvite(inviteId: string, now = Date.now()): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ inviteId, at: now }));
  } catch {
    // private mode / quota — the sign-in returnUrl still carries the invite path
  }
}

export function readPendingGroupInvite(now = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { inviteId?: unknown; at?: unknown };
    if (typeof parsed.inviteId !== 'string' || typeof parsed.at !== 'number' || now - parsed.at > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return parsed.inviteId;
  } catch {
    return null;
  }
}

export function clearPendingGroupInvite(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // nothing to clear
  }
}

/**
 * Phase 10a: the same trip for a group's invite LINK (`/group-join/<token>`) — its own key, the same day's expiry.
 * `usePendingGroupResume` brings the reader back; a final state on the join page clears it.
 */
const JOIN_KEY = 'symphony-pending-group-join';

export function savePendingGroupJoin(token: string, now = Date.now()): void {
  try {
    window.localStorage.setItem(JOIN_KEY, JSON.stringify({ token, at: now }));
  } catch {
    // private mode / quota — the sign-in returnUrl still carries the link path
  }
}

export function readPendingGroupJoin(now = Date.now()): string | null {
  try {
    const raw = window.localStorage.getItem(JOIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof parsed.token !== 'string' || typeof parsed.at !== 'number' || now - parsed.at > MAX_AGE_MS) {
      window.localStorage.removeItem(JOIN_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    return null;
  }
}

export function clearPendingGroupJoin(): void {
  try {
    window.localStorage.removeItem(JOIN_KEY);
  } catch {
    // nothing to clear
  }
}

/**
 * Where a reader with a parked group invite or join link goes once they are signed in and have a Symphony account, or
 * null when nothing should move them. Pure, so the return trip is testable without the hook (`usePendingGroupResume`).
 * - `accountReady` false (no Symphony account yet, or still resolving): stay — the trip resumes once it exists.
 * - 2026-11-02: NOT held until onboarding finishes. A person invited to a group came for the group; holding the invite
 *   behind the whole onboarding chat sent a new user into a generic greeting first (and dropped the invite after a day
 *   of not finishing). The greeting does not hijack them back: it fires only on a bare home with no chat open, and an
 *   unfinished onboarding is resumed later from the home, as for anyone.
 * - On `/welcome`, which is creating the account: stay, it lands on the home and the trip runs from there.
 * - Already on the page the pending item names: stay, that page owns it now.
 * - Both parked: the per-person invite first (it names the reader; the join link is anyone's), then the join link
 *   on the next arrival, once the invite page cleared its own key.
 */
export function pendingGroupResumeHref(pathname: string, accountReady: boolean, now = Date.now()): string | null {
  if (!accountReady) return null;
  if (pathname.startsWith('/welcome')) return null;
  if (pathname.startsWith('/group-invite') || pathname.startsWith('/group-join')) return null;
  const invite = readPendingGroupInvite(now);
  if (invite) return `/group-invite/${encodeURIComponent(invite)}`;
  const join = readPendingGroupJoin(now);
  if (join) return `/group-join/${encodeURIComponent(join)}`;
  return null;
}
